import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { X } from "lucide-react";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";

export function AddTextContentDialog({ open, onOpenChange, onAddContent }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const contentRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "pasted-text",
    content: "",
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        title: "pasted-text",
        content: "",
      });

      // Focus content field after a short delay
      const timer = setTimeout(() => {
        contentRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onOpenChange(false);
  };

  const sanitizeFilename = (title) => {
    return title
      .replace(/[^a-zA-Z0-9-_]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!formData.content.trim()) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Create filename
      const filename = formData.title.trim()
        ? `${sanitizeFilename(formData.title)}.txt`
        : `text-content-${Date.now()}.txt`;

      // Create File object (pattern from useFileUpload.js lines 503-507)
      const blob = new Blob([formData.content], { type: "text/plain" });
      const file = new File([blob], filename, { type: "text/plain" });

      // Create synthetic event matching handleFileChange expectations
      const syntheticEvent = {
        target: { files: [file] },
        preventDefault: () => {},
        stopPropagation: () => {},
      };

      onAddContent(syntheticEvent);

      // Close dialog after successful submission
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (
      e.key === "Enter" &&
      e.ctrlKey &&
      formData.content.trim() &&
      !isSubmitting
    ) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === "Escape" && !isSubmitting) {
      e.preventDefault();
      handleClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[90vw] max-h-[90vh] w-[500px] h-auto bg-white dark:bg-zinc-900 border-gray-200 dark:border-[#3a3a3a] text-gray-900 dark:text-white p-0 gap-0 rounded-2xl flex flex-col overflow-hidden shadow-2xl"
        hideCloseButton={true}
        onEscapeKeyDown={(e) => isSubmitting && e.preventDefault()}
        onPointerDownOutside={(e) => isSubmitting && e.preventDefault()}
        onCloseAutoFocus={(e) => {
          // Prevent focus from going to a non-existent trigger element
          e.preventDefault();
          // Blur any focused element to release the focus trap
          setTimeout(() => {
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
          }, 0);
        }}
      >
        <DialogTitle className="sr-only">Add text content</DialogTitle>
        <DialogDescription className="sr-only">
          Create a text file attachment from typed or pasted content
        </DialogDescription>

        {/* Header */}
        <div className="flex items-center justify-between py-3 px-4 bg-white dark:bg-zinc-900 flex-shrink-0 border-b border-gray-100 dark:border-white/5">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Add text content
          </h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500 dark:text-zinc-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-900">
          <div className="flex-1 overflow-auto px-4 py-4 space-y-4">
            <div className="space-y-1.5">
              <label
                htmlFor="text-content-title"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Title
              </label>
              <input
                id="text-content-title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="pasted-text"
                disabled={isSubmitting}
                className="h-9 w-full rounded-lg border border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-gray-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-purple-400/50 transition-all"
              />
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="text-content-content"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Content
              </label>
              <textarea
                ref={contentRef}
                id="text-content-content"
                name="content"
                value={formData.content}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="Type or paste in content..."
                disabled={isSubmitting}
                rows={6}
                className="w-full resize-none rounded-lg border border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-gray-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-purple-400/50 transition-all"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50/50 dark:bg-zinc-900/50 border-t border-gray-100 dark:border-white/5 flex-shrink-0">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isSubmitting}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-zinc-800 px-3 py-2 rounded-lg text-sm font-medium"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.content.trim()}
              className="bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 px-4 rounded-full text-sm font-semibold h-9 shadow-sm transition-all"
            >
              Add Content
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

AddTextContentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onAddContent: PropTypes.func.isRequired,
};

export default AddTextContentDialog;
